/**
 * Pixel-grid loader with a shimmering label, for long non-streaming waits
 * where nothing is arriving on screen yet.
 *
 * Adapted from beautifului.dev's LoadingState: the Surfer variant (needs a video
 * asset) and the elapsed timer are dropped, and the label shimmer reuses
 * `.trace-shimmer` so it matches the thinking trace.
 *
 * Each of the 9 cells shows one ninth of the app icon (via background-position),
 * so the grid reassembles the mark as it pulses.
**/

import icon from "@/assets/icon.png";

/* A chevron wavefront: delay grows with distance from the middle-left cell. */
const CHEVRON = Array.from({ length: 9 }, (_, index) => {
  const row = Math.floor(index / 3);
  const column = index % 3;
  return (column + Math.abs(row - 1)) * 90;
});

/* A single cell lapping the perimeter; the centre never lights. */
const ORBIT_ORDER = [0, 1, 2, 5, 8, 7, 6, 3];
const ORBIT = Array.from({ length: 9 }, (_, index) => {
  const step = ORBIT_ORDER.indexOf(index);
  return step === -1 ? null : step * 110;
});

type Pattern = { delays: (number | null)[]; duration: number };

const PATTERNS: Record<string, Pattern> = {
  Drive: { delays: CHEVRON, duration: 650 },
  Orbit: { delays: ORBIT, duration: 950 },
};

/* Cell edge in px; drives both the grid template and each cell's w/h so they
   can't drift apart. Sized so the mark stays legible once split across 9 tiles. */
const CELL_SIZE = 6;

const LoaderGrid = ({ delays, duration, cellSize = CELL_SIZE }: Pattern & { cellSize?: number }) => (
  <span
    aria-hidden
    // className="grid shrink-0 gap-[2px]"
    className="grid shrink-0"
    style={{ gridTemplateColumns: `repeat(3, ${cellSize}px)` }}
  >
    {delays.map((delay, index) => {
      const idle = delay === null;

      return (
        <span
          key={index}
          className="pixel-cell rounded-[1px]"
          style={{
            width: cellSize,
            height: cellSize,
            opacity: idle ? 0.15 : 0.3,
            animation: idle ? "none" : `pixel-on ${duration}ms ease-in-out ${delay}ms infinite`,
            backgroundImage: `url(${icon})`,
            backgroundSize: "300% 300%",
            backgroundPosition: `${(index % 3) * 50}% ${Math.floor(index / 3) * 50}%`,
          }}
        />
      );
    })}
  </span>
);

interface LoadingStateProps {
  label: string;
  variant?: keyof typeof PATTERNS;
  /* Overrides the default edge length; for tuning, not for call sites. */
  cellSize?: number;
}

const LoadingState = ({ label, variant = "Drive", cellSize }: LoadingStateProps) => {
  const pattern = PATTERNS[variant] ?? PATTERNS.Drive;

  return (
    <div role="status" className="flex w-fit items-center gap-2.5">
      <LoaderGrid {...pattern} cellSize={cellSize} />
      <span className="trace-shimmer text-[13px] font-medium">{label}</span>
    </div>
  );
};

export default LoadingState;
